import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";

// Sub-components
import { AppNameSection } from "../components/settings/AppNameSection";
import { CategoriesSection } from "../components/settings/CategoriesSection";
import { SecuritySection } from "../components/settings/SecuritySection";
import { DataManagementSection } from "../components/settings/DataManagementSection";
import { AboutSection } from "../components/settings/AboutSection";
import { CategoryFormDialog } from "../components/settings/CategoryFormDialog";
import { ResetPinDialog } from "../components/settings/ResetPinDialog";

export default function SettingsPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(false);

  // App name state
  const [appName, setAppName] = useState("FamilySync");
  const [editingAppName, setEditingAppName] = useState(false);
  const [tempAppName, setTempAppName] = useState("");

  // Shopping categories state
  const [categories, setCategories] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", color: "#6366f1" });

  const fetchAppSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setAppName(res.data.app_name || "FamilySync");
    } catch (error) {
      console.error("Failed to fetch settings");
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/shopping/categories?include_inactive=${showInactive}`
      );
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  }, [showInactive]);

  useEffect(() => {
    fetchAppSettings();
  }, [fetchAppSettings]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSaveAppName = async () => {
    if (!tempAppName.trim()) {
      toast.error("App name cannot be empty");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.put(`${API}/settings`, {
        app_name: tempAppName.trim(),
      });
      setAppName(res.data.app_name);
      setEditingAppName(false);
      toast.success("App name updated! Refresh to see changes everywhere.");
      window.dispatchEvent(
        new CustomEvent("appNameChanged", { detail: res.data.app_name })
      );
    } catch (error) {
      toast.error("Failed to update app name");
    } finally {
      setLoading(false);
    }
  };

  const startEditingAppName = () => {
    setTempAppName(appName);
    setEditingAppName(true);
  };

  const handleResetPin = async () => {
    if (newPin.length < 4) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("PINs do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset`, { pin: newPin });
      toast.success("PIN reset successfully");
      setPinModalOpen(false);
      setNewPin("");
      setConfirmPin("");
    } catch (error) {
      toast.error("Failed to reset PIN");
    } finally {
      setLoading(false);
    }
  };

  const handleClearData = async () => {
    toast.success("Data cleared successfully");
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Category handlers
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ name: category.name, color: category.color });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: "", color: "#6366f1" });
    }
    setCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setEditingCategory(null);
    setCategoryForm({ name: "", color: "#6366f1" });
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    setLoading(true);
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/shopping/categories/${editingCategory.id}`,
          categoryForm
        );
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/shopping/categories`, categoryForm);
        toast.success("Category created");
      }
      fetchCategories();
      closeCategoryModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      await axios.put(`${API}/shopping/categories/${category.id}`, {
        active: !category.active,
      });
      toast.success(
        category.active ? "Category deactivated" : "Category reactivated"
      );
      fetchCategories();
    } catch (error) {
      toast.error("Failed to update category");
    }
  };

  const handleDeleteCategory = async (category) => {
    try {
      const res = await axios.delete(
        `${API}/shopping/categories/${category.id}`
      );
      if (res.data.deactivated) {
        toast.success(res.data.message);
        setShowInactive(true);
      } else {
        toast.success("Category deleted");
      }
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white font-['Outfit']">
          Settings
        </h1>
        <p className="text-slate-400 mt-1">Manage your FamilySync preferences</p>
      </div>

      {/* Settings Sections */}
      <div className="space-y-4">
        <AppNameSection
          appName={appName}
          editingAppName={editingAppName}
          tempAppName={tempAppName}
          setTempAppName={setTempAppName}
          loading={loading}
          onStartEditing={startEditingAppName}
          onSave={handleSaveAppName}
          onCancel={() => setEditingAppName(false)}
        />

        <CategoriesSection
          categories={categories}
          showInactive={showInactive}
          onToggleShowInactive={() => setShowInactive(!showInactive)}
          onAddCategory={() => openCategoryModal()}
          onEditCategory={openCategoryModal}
          onToggleActive={handleToggleActive}
          onDeleteCategory={handleDeleteCategory}
        />

        <SecuritySection
          onResetPin={() => setPinModalOpen(true)}
          onLogout={handleLogout}
        />

        <DataManagementSection onClearData={handleClearData} />

        <AboutSection appName={appName} />
      </div>

      {/* Reset PIN Dialog */}
      <ResetPinDialog
        open={pinModalOpen}
        onOpenChange={setPinModalOpen}
        newPin={newPin}
        setNewPin={setNewPin}
        confirmPin={confirmPin}
        setConfirmPin={setConfirmPin}
        onSubmit={handleResetPin}
        loading={loading}
      />

      {/* Category Form Dialog */}
      <CategoryFormDialog
        open={categoryModalOpen}
        onOpenChange={setCategoryModalOpen}
        formData={categoryForm}
        setFormData={setCategoryForm}
        onSubmit={handleSaveCategory}
        isEditing={!!editingCategory}
        loading={loading}
      />
    </div>
  );
}
